"use client";

import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Trash2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
interface Project {
  id: string;
  name: string;
  is_saved?: boolean;
  project_id?: string;
}
interface ProjectSelectionProps {
  onProjectSelect: (projectId: string | null, isSaved: boolean) => void;
  selectedProjectId: string | null;
}
export function ProjectSelection({
  onProjectSelect,
  selectedProjectId
}: ProjectSelectionProps) {
  const [projects, setProjects] = useState<Project[]>([]);
  const [newProjectName, setNewProjectName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [userId, setUserId] = useState<string | null>(null);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [editingName, setEditingName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const getUser = async () => {
      const {
        data: {
          session
        }
      } = await supabase.auth.getSession();
      if (session?.user) {
        setUserId(session.user.id);
      }
    };
    getUser();
  }, []);
  const fetchProjects = async () => {
    const {
      data,
      error
    } = await supabase.from('project_requirements').select('id, name, is_saved').order('created_at', {
      ascending: false
    });
    if (error) {
      console.error('Error fetching projects:', error);
      toast.error("Failed to fetch projects");
      return;
    }
    const formattedData = data?.map(project => ({
      id: project.id,
      name: project.name,
      is_saved: project.is_saved
    })) || [];
    setProjects(formattedData);

    // If there's a selectedProjectId, make sure it still exists in the updated list
    if (selectedProjectId) {
      const projectExists = formattedData.some(project => project.id === selectedProjectId);
      if (!projectExists) {
        // If the selected project no longer exists, clear the selection
        onProjectSelect(null, false);
      }
    }
  };
  useEffect(() => {
    fetchProjects();
  }, []);
  const handleProjectSelect = (projectId: string, isSaved: boolean) => {
    onProjectSelect(projectId, isSaved);
  };
  const createProject = async () => {
    if (!newProjectName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    if (!userId) {
      toast.error("You must be logged in to create a project");
      return;
    }
    try {
      // First create the project requirements entry
      const {
        data: projectData,
        error: projectError
      } = await supabase.from('project_requirements').insert({
        name: newProjectName.trim(),
        user_id: userId,
        directors_count: 0,
        managers_count: 0,
        team_members_count: 0,
        organizational_structure: 'hierarchical',
        is_saved: false,
        project_id: crypto.randomUUID() // Generate a unique project_id
      }).select().single();
      if (projectError) {
        console.error('Error creating project:', projectError);
        toast.error("Failed to create project");
        return;
      }
      toast.success("Project created successfully");
      setNewProjectName("");
      setIsCreating(false);
      if (projectData.id) {
        onProjectSelect(projectData.id, false);
      }
      fetchProjects(); // Refresh the projects list
    } catch (error) {
      console.error('Error in createProject:', error);
      toast.error("Failed to create project");
    }
  };
  const deleteProject = async (project: Project) => {
    if (!project?.id) {
      console.error('Invalid project ID');
      toast.error("Cannot delete project: Invalid project ID");
      return;
    }
    try {
      // First get the project_id from project_requirements
      const {
        data: projectData,
        error: projectError
      } = await supabase.from('project_requirements').select('project_id').eq('id', project.id).maybeSingle();
      if (projectError) {
        console.error('Error fetching project data:', projectError);
        toast.error("Failed to fetch project data");
        return;
      }
      if (!projectData?.project_id) {
        console.error('No project_id found');
        toast.error("Project data not found");
        return;
      }
      const projectId = projectData.project_id;
      console.log('Attempting to delete project and related records with project_id:', projectId);

      // Delete records sequentially to avoid race conditions
      // 1. First delete candidates as they have no dependencies
      const {
        error: candidatesError
      } = await supabase.from('candidates').delete().eq('project_id', projectId);
      if (candidatesError) {
        console.error('Error deleting candidates:', candidatesError);
        throw new Error('Failed to delete candidates');
      }

      // 2. Delete role_jd entries
      const {
        error: roleJdError
      } = await supabase.from('role_jd').delete().eq('project_id', projectId);
      if (roleJdError) {
        console.error('Error deleting role JDs:', roleJdError);
        throw new Error('Failed to delete role JDs');
      }

      // 3. Delete nodes
      const {
        error: nodesError
      } = await supabase.from('nodes').delete().eq('project_id', projectId);
      if (nodesError) {
        console.error('Error deleting nodes:', nodesError);
        throw new Error('Failed to delete nodes');
      }

      // 4. Delete organization structure
      const {
        error: orgStructError
      } = await supabase.from('organization_structures').delete().eq('project_id', projectId);
      if (orgStructError) {
        console.error('Error deleting organization structures:', orgStructError);
        throw new Error('Failed to delete organization structure');
      }

      // 5. Finally delete the project from project_requirements
      const {
        error: reqError
      } = await supabase.from('project_requirements').delete().eq('id', project.id);
      if (reqError) {
        console.error('Error deleting project requirements:', reqError);
        throw new Error('Failed to delete project requirements');
      }

      // After successful deletion, fetch the updated project list
      await fetchProjects();

      // Update selected project if needed
      if (selectedProjectId === project.id) {
        onProjectSelect(null, false);
      }
      setProjectToDelete(null);
      toast.success("Project and all related data deleted successfully");
    } catch (error) {
      console.error('Error in deleteProject:', error);
      toast.error(error instanceof Error ? error.message : "Failed to delete project");
    }
  };
  const updateProjectName = async () => {
    if (!editingProject?.id) {
      console.error('Invalid project ID for update');
      toast.error("Cannot update project: Invalid project ID");
      return;
    }
    if (!editingName.trim()) {
      toast.error("Please enter a project name");
      return;
    }
    try {
      const {
        error
      } = await supabase.from('project_requirements').update({
        name: editingName.trim(),
        updated_at: new Date().toISOString()
      }).eq('id', editingProject.id);
      if (error) {
        console.error('Error updating project name:', error);
        toast.error("Failed to update project name");
        return;
      }
      toast.success("Project name updated successfully");
      setEditingProject(null);
      setEditingName("");

      // Fetch updated project list
      await fetchProjects();
    } catch (error) {
      console.error('Error in updateProjectName:', error);
      toast.error("Failed to update project name");
    }
  };
  const calculateEmptyRows = () => {
    const targetRows = 10;
    const currentRows = projects.length;
    return Math.max(0, targetRows - currentRows);
  };
  const emptyRows = calculateEmptyRows();
  useEffect(() => {
    if (isCreating && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isCreating]);
  return <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 p-4 h-full transition-all duration-300 hover:shadow-yellow-400/20">
      <h3 className="text-xl font-semibold mb-0 py-2 text-white">Select Project</h3>
      
      <div className="space-y-3 h-[calc(100%-4rem)] flex flex-col">
        {!isCreating ? <Button onClick={() => setIsCreating(true)} className="w-[200px] justify-start text-black bg-primary">
            <Plus className="mr-2 h-4 w-4" />
            Create New Project
          </Button> : <div className="space-y-2">
            <Input ref={inputRef} value={newProjectName} onChange={e => setNewProjectName(e.target.value)} placeholder="Enter project name" className="w-full text-sm text-white font-normal" onKeyDown={e => {
          if (e.key === 'Enter') {
            createProject();
          }
        }} />
            <div className="flex gap-2">
              <Button onClick={createProject} className="flex-1">
                Create
              </Button>
              <Button onClick={() => {
            setIsCreating(false);
            setNewProjectName("");
          }} variant="outline" className="flex-1">
                Cancel
              </Button>
            </div>
          </div>}

        <div className="flex-1 overflow-hidden">
          {projects.length === 0 && !isCreating && <p className="text-gray-400 text-xs text-center py-2">
              No projects yet. Create your first project to get started!
            </p>}
          <div className="relative h-full">
            <div className="absolute inset-0 overflow-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-secondary backdrop-blur-lg z-10">
                  <TableRow>
                    <TableHead className="w-[10%] text-primary-light text-xs py-2">SN</TableHead>
                    <TableHead className="w-[75%] text-primary-light text-xs text-left py-2">Project Name</TableHead>
                    <TableHead className="w-[15%] text-primary-light text-xs py-2">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {projects.map((project, index) => <TableRow key={project.id} className={`text-xs ${selectedProjectId === project.id ? "bg-[#F1F1F1] hover:bg-[#F1F1F1]/90" : "hover:bg-primary/5"} cursor-pointer transition-colors duration-200`} onClick={() => handleProjectSelect(project.id, project.is_saved || false)}>
                      <TableCell className={`font-mono text-xs py-1 ${selectedProjectId === project.id ? "text-black" : "text-white"}`}>
                        {index + 1}
                      </TableCell>
                      <TableCell className={`py-1 text-left ${selectedProjectId === project.id ? "text-black" : ""}`}>
                        <span className={`text-xs font-normal ${selectedProjectId === project.id ? "text-black" : "text-white"}`}>
                          {project.name}
                        </span>
                      </TableCell>
                      <TableCell className="py-1">
                        <div className="flex items-center justify-end gap-2">
                          <Pencil className="h-3 w-3 cursor-pointer text-primary hover:text-primary transition-colors" onClick={e => {
                        e.stopPropagation();
                        setEditingProject(project);
                        setEditingName(project.name);
                      }} />
                          <Trash2 className="h-3 w-3 text-primary cursor-pointer hover:text-primary transition-colors" onClick={e => {
                        e.stopPropagation();
                        setProjectToDelete(project);
                      }} />
                        </div>
                      </TableCell>
                    </TableRow>)}
                  {Array.from({
                  length: calculateEmptyRows()
                }).map((_, index) => <TableRow key={`empty-${index}`} className="text-xs h-6">
                      <TableCell className="text-xs text-white py-1">{projects.length + index + 1}</TableCell>
                      <TableCell className="text-xs text-white text-left py-1">&nbsp;</TableCell>
                      <TableCell className="text-xs text-white py-1">&nbsp;</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!editingProject} onOpenChange={open => !open && setEditingProject(null)}>
        <DialogContent className="bg-secondary border-primary/20">
          <DialogHeader>
            <DialogTitle className="text-primary">Edit Project Name</DialogTitle>
            <DialogDescription className="text-gray-300">
              Enter a new name for your project.
            </DialogDescription>
          </DialogHeader>
          <Input value={editingName} onChange={e => setEditingName(e.target.value)} placeholder="Enter new project name" className="mt-4" onKeyDown={e => {
          if (e.key === 'Enter') {
            updateProjectName();
          }
        }} />
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => {
            setEditingProject(null);
            setEditingName("");
          }} className="bg-secondary text-primary border-primary/20 hover:bg-primary/10">
              Cancel
            </Button>
            <Button onClick={updateProjectName} className="bg-primary hover:bg-primary/90">
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!projectToDelete} onOpenChange={open => !open && setProjectToDelete(null)}>
        <AlertDialogContent className="bg-secondary border-primary/20">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-primary">Delete Project</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-300">
              Are you sure you want to delete "{projectToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setProjectToDelete(null)} className="bg-secondary text-primary border-primary/20 hover:bg-primary/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={() => {
            if (projectToDelete) {
              deleteProject(projectToDelete);
            }
          }} className="bg-red-600 hover:bg-red-700 text-white">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>;
}